// Spec rev150 (2026-02-07) — helper partagé rendu deadline enrichie.
// Extrait de TaskListView.jsx pour partage avec TaskKanbanView et
// tout composant Task qui affiche une échéance.
//
// Palette (rev150) :
//   overdue : rouge  (#e07a7a sur #3a1414) · « · Retard · Xj »
//   near    : orange (#e0a55a sur #2a2410) · « · Dans Xj »  (0 ≤ Δ ≤ 7)
//   comfort : vert   (#5ab87a sur #0f2818) · « · Dans Xj »  (Δ > 7)
//   none    : gris   (#5a5a5f sur #1a1a1e) · « Aucune échéance »
//
// Spec rev151 (2026-02-07) — Le badge affiche désormais un tooltip au
// survol donnant l'heure précise + le fuseau horaire local
// (ex. "10:00 (Europe/Algiers, UTC+01:00)"), utile aux tâches à
// deadline courte dans la journée.
import { useEffect, useState } from "react";
import { Clock, CalendarOff, AlertCircle, Lock as LockIcon } from "lucide-react";

function _daysDelta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const startTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const now = new Date();
  const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((startTarget - startNow) / 86_400_000);
}

export function deadlineMeta(iso) {
  const delta = _daysDelta(iso);
  if (delta === null) {
    return { state: "none", iconColor: "#5a5a5f", bg: "#1a1a1e", label: "Aucune échéance", relative: "", Icon: CalendarOff };
  }
  if (delta < 0) {
    const jr = Math.abs(delta);
    return { state: "overdue", iconColor: "#e07a7a", bg: "#3a1414", label: null, relative: `· Retard · ${jr}j`, Icon: Clock };
  }
  if (delta <= 7) {
    return { state: "near", iconColor: "#e0a55a", bg: "#2a2410", label: null, relative: `· Dans ${delta}j`, Icon: Clock };
  }
  return { state: "comfort", iconColor: "#5ab87a", bg: "#0f2818", label: null, relative: `· Dans ${delta}j`, Icon: Clock };
}

export const fmtDateShort = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", {
  day: "2-digit", month: "short", year: "2-digit",
}) : "");

/** iter424 pt 5 — retard figé à la date de fin de l'exécutant : { late, days, color, doneLabel }. */
export function frozenLateness(deadlineIso, doneIso) {
  const d = new Date(deadlineIso), f = new Date(doneIso);
  if (isNaN(d.getTime()) || isNaN(f.getTime())) return null;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(f) - day(d)) / 86_400_000);
  const late = days > 0;
  return { late, days, color: late ? "#8A8A93" : "#22C55E", doneLabel: f.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) };
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—");
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
}).replace(",", "") : "—");

/**
 * deadlineTooltip — texte multi-ligne pour le title HTML.
 * Utilise Intl.DateTimeFormat pour extraire le fuseau horaire local
 * (ex. "Europe/Algiers") + calcule l'offset UTC humain (ex. "UTC+01:00").
 */
export function deadlineTooltip(iso) {
  if (!iso) return "Sans échéance";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Sans échéance";
  const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || "local";
  // Offset en minutes → format ±HH:MM.
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? "+" : "-";
  const abs = Math.abs(offMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  const utc = `UTC${sign}${hh}:${mm}`;
  const hourStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  // Spec iter266 point 6 (2026-02) — mois abrégé cohérent (« sept. »).
  const dateStr = d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
  // Spec rev155 (2026-02-07) — compteur temps réel pour les tâches
  // "near" (≤ 7 jours). Précision jours + heures + minutes. Rafraîchi
  // à chaque re-render (voir hook useMinuteTick dans DeadlineInline /
  // DeadlineCell qui déclenche un re-render par minute).
  const countdown = _countdownDetail(iso);
  // Spec rev160 (2026-02-07) — Repère « ⚠ Aujourd'hui » ou « Demain
  // HH:MM » quand l'échéance tombe dans les prochaines 24h. Complète
  // le compteur détaillé pour rendre l'urgence intuitive : distingue
  // « demain matin » d'« après-demain soir » d'un coup d'œil.
  const now = Date.now();
  const diffMs = d.getTime() - now;
  let dayHint = "";
  if (diffMs > 0 && diffMs <= 24 * 3_600_000) {
    const today = new Date();
    const isSameDay = d.getFullYear() === today.getFullYear()
      && d.getMonth() === today.getMonth()
      && d.getDate() === today.getDate();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const isTomorrow = d.getFullYear() === tomorrow.getFullYear()
      && d.getMonth() === tomorrow.getMonth()
      && d.getDate() === tomorrow.getDate();
    if (isSameDay) dayHint = `⚠ Aujourd'hui · ${hourStr}`;
    else if (isTomorrow) dayHint = `Demain · ${hourStr}`;
    else dayHint = `Dans ≤ 24h · ${hourStr}`;
  }
  const lines = [dateStr, `${hourStr} (${tz}, ${utc})`];
  if (dayHint) lines.push(dayHint);
  if (countdown) lines.push(countdown);
  return lines.join("\n");
}

/** Compteur détaillé "Dans 2j 3h 45m" ou "Retard 1j 4h" ou "" (hors near). */
function _countdownDetail(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const totalMin = Math.floor(abs / 60_000);
  const j = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  const parts = [];
  if (j > 0) parts.push(`${j}j`);
  if (h > 0) parts.push(`${h}h`);
  if (j === 0) parts.push(`${m}m`);
  const label = parts.join(" ") || "moins d'1 min";
  return diffMs < 0 ? `Retard · ${label}` : `Dans ${label}`;
}

// Spec rev155 (2026-02-07) — micro-hook local pour re-rendre le
// composant toutes les 60 s. Utilisé UNIQUEMENT pour les deadlines
// "near" (≤ 7 j) où le compteur détaillé mérite d'être frais.
function useMinuteTick(enabled) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [enabled]);
}

/**
 * DeadlineCell — rendu compact pour une cellule de tableau (Liste).
 * Props :
 *   • iso       : ISO date string ou null
 *   • closedAt  : ISO string si la tâche est clôturée → ligne verte dessous
 */
export function DeadlineCell({ iso, closedAt }) {
  const meta = deadlineMeta(iso);
  const { Icon, iconColor, bg, relative, label, state } = meta;
  // Rev155 — rafraîchit le title chaque minute quand deadline "near"
  // ou "overdue" (compteur détaillé pertinent). Pas de tick pour
  // "comfort" ni "none" (perte inutile de perfs).
  useMinuteTick(state === "near" || state === "overdue");
  const tooltip = deadlineTooltip(iso);
  return (
    <div className="flex flex-col gap-1 leading-tight">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center shrink-0"
          style={{
            width: 26, height: 26,
            background: bg,
            borderRadius: 7,
            color: iconColor,
          }}
          data-testid={`deadline-badge-${state}`}
          title={tooltip}
        >
          <Icon size={14} strokeWidth={2.2} />
        </span>
        {state === "none" ? (
          <span className="text-[12px]" style={{ color: "#8B8F94" }} title={tooltip}>
            {label}
          </span>
        ) : (
          <span className="text-[12px] tabular-nums whitespace-nowrap" title={tooltip}>
            <span style={{ color: "#F2F2F2", fontWeight: 600 }}>{fmtDateShort(iso)}</span>
            {relative && (
              <span style={{ color: iconColor, marginLeft: 6, fontWeight: 500 }}>
                {relative}
              </span>
            )}
          </span>
        )}
      </div>
      {closedAt && (
        <span
          className="text-[10px] tabular-nums"
          style={{ color: "var(--status-cloture-tasks)", paddingLeft: 34 }}
          title={`Clôturée le ${fmt(closedAt)}`}
        >
          Clôturée · {fmtShort(closedAt)}
        </span>
      )}
    </div>
  );
}

/**
 * DeadlineInline — variante réduite pour cartes/Kanban.
 * Rend le même badge coloré + date + mention relative, mais sans la
 * ligne "Clôturée" ni le wrapping flex-col, adapté à un footer de carte.
 * Props :
 *   • iso  : ISO date string ou null
 *   • size : taille du badge (défaut 26)
 */
export function DeadlineInline({ iso, size = 26, doneAt = null }) {
  const meta = deadlineMeta(iso);
  const { Icon, iconColor, bg, relative, label, state } = meta;
  useMinuteTick(!doneAt && (state === "near" || state === "overdue"));
  const tooltip = deadlineTooltip(iso);
  const badgeSize = size;
  const iconSize = Math.max(11, Math.round(size * 0.55));
  // iter424 pt 5 — retard figé : l'exécutant a terminé, le compteur s'arrête à cette date.
  const frozen = iso && doneAt ? frozenLateness(iso, doneAt) : null;
  if (frozen) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`deadline-inline-done-${frozen.late ? "late" : "ontime"}`} title={`Terminée le ${frozen.doneLabel}${frozen.late ? ` avec ${frozen.days} j de retard` : " dans les délais"}`}>
        <span className="inline-flex items-center justify-center shrink-0" style={{ width: badgeSize, height: badgeSize, background: frozen.late ? "#1f1f24" : "#0f2818", borderRadius: 7, color: frozen.color }}>
          <Icon size={iconSize} strokeWidth={2.2} />
        </span>
        <span className="text-[11.5px] tabular-nums">
          <span style={{ color: "#F2F2F2", fontWeight: 600 }}>{fmtDateShort(iso)}</span>
          <span style={{ color: frozen.color, marginLeft: 5, fontWeight: 500 }}>{frozen.late ? `· Terminée avec ${frozen.days} j de retard` : "· Terminée à temps"}</span>
        </span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
      data-testid={`deadline-inline-${state}`}
      title={tooltip}
    >
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: badgeSize, height: badgeSize,
          background: bg,
          borderRadius: 7,
          color: iconColor,
        }}
      >
        <Icon size={iconSize} strokeWidth={2.2} />
      </span>
      {state === "none" ? (
        <span className="text-[11.5px]" style={{ color: "#8B8F94" }}>{label}</span>
      ) : (
        <span className="text-[11.5px] tabular-nums">
          <span style={{ color: "#F2F2F2", fontWeight: 600 }}>{fmtDateShort(iso)}</span>
          {relative && (
            <span style={{ color: iconColor, marginLeft: 5, fontWeight: 500 }}>
              {relative}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * DeadlineCellCompact — variante minimaliste pour tableaux dense
 * (spec rev156 — TaskListView).
 *
 * Objectif : réduire la largeur nécessaire pour ne plus empiéter sur
 * la colonne Pièces jointes à droite.
 *
 * Règles :
 *   • near / comfort (deadline à venir) : icône horloge + date courte
 *     « 10 août 26 » — SANS mention « Dans Xj » (info portée par la
 *     couleur uniquement, tooltip contient le détail temps-réel).
 *   • overdue : icône alert-circle rouge + « Retard · Xj » à la place
 *     de la date (info d'urgence prioritaire sur la date exacte).
 *   • none : texte gris « Aucune échéance » SANS icône.
 *
 * La ligne « Clôturée · XX » est conservée en dessous quand
 * ``closedAt`` existe (rétrocompat totale avec DeadlineCell).
 */
export function DeadlineCellCompact({ iso, closedAt, doneAt = null }) {
  // Spec iter114 (Feb 2026) — hooks toujours appelés en tête (rules-of-hooks).
  const meta = deadlineMeta(iso);
  const { iconColor, state } = meta;
  // Le tick minute reste utile en cas d'overdue/near sur tâche non clôturée.
  useMinuteTick(!doneAt && (state === "near" || state === "overdue"));

  // iter424 pt 5 — retard figé : « 53 j de retard » en gris (#8A8A93) ; survol « Terminée le JJ/MM/AAAA avec 53 j de retard ».
  const frozen = iso && doneAt ? frozenLateness(iso, doneAt) : null;
  if (frozen && frozen.late) {
    return (
      <div className="inline-flex items-center gap-1.5 whitespace-nowrap" title={`Terminée le ${frozen.doneLabel} avec ${frozen.days} j de retard`} data-testid="deadline-compact-done-late">
        <Clock size={13} strokeWidth={2.2} style={{ color: "#8A8A93" }} />
        <span className="text-[12px] tabular-nums font-semibold" style={{ color: "#8A8A93" }}>{frozen.days} j de retard</span>
      </div>
    );
  }
  if (frozen && !closedAt) {
    return (
      <div className="inline-flex items-center gap-1.5 whitespace-nowrap" title={`Terminée le ${frozen.doneLabel} dans les délais`} data-testid="deadline-compact-done-ontime">
        <Clock size={13} strokeWidth={2.2} style={{ color: "#22C55E" }} />
        <span className="text-[12px] tabular-nums font-semibold" style={{ color: "#22C55E" }}>Terminée à temps</span>
      </div>
    );
  }

  // Cohérence avec Documents/AVI : quand la tâche est clôturée, on affiche
  // UNIQUEMENT le cadenas + date de clôture (couleur verte #22C55E) — plus
  // jamais de « Retard · Xj » empilé au-dessus qui rendait la cellule
  // illisible (bug user report).
  if (closedAt) {
    const closedDate = new Date(closedAt);
    return (
      <div
        className="inline-flex items-center gap-1.5 whitespace-nowrap"
        title={`Clôturée le ${closedDate.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
        data-testid="deadline-compact-closed"
      >
        <LockIcon size={13} strokeWidth={2.4} style={{ color: "var(--status-cloture-tasks)" }} />
        <span
          className="text-[12px] tabular-nums font-semibold"
          style={{ color: "var(--status-cloture-tasks)" }}
        >
          {closedDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })}
        </span>
      </div>
    );
  }

  const tooltip = deadlineTooltip(iso);
  // Icône : AlertCircle en overdue, Clock en near/comfort, aucune en none.
  const RenderIcon = state === "overdue" ? AlertCircle : Clock;

  return (
    <div className="flex flex-col leading-tight">
      <div
        className="inline-flex items-center gap-1.5 whitespace-nowrap"
        title={tooltip}
        data-testid={`deadline-compact-${state}`}
      >
        {state === "none" ? (
          <span className="text-[12px]" style={{ color: "#8B8F94" }}>
            Aucune échéance
          </span>
        ) : state === "overdue" ? (
          <>
            <RenderIcon size={13} strokeWidth={2.2} style={{ color: iconColor }} />
            <span className="text-[12px] tabular-nums font-semibold" style={{ color: iconColor }}>
              Retard · {Math.abs(_daysDelta(iso))}j
            </span>
          </>
        ) : (
          <>
            <RenderIcon size={13} strokeWidth={2.2} style={{ color: iconColor }} />
            <span className="text-[12px] tabular-nums" style={{ color: "#F2F2F2", fontWeight: 500 }}>
              {fmtDateShort(iso)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

